import { createReducer } from './utils';

import {
  handleAvailable,
  handleRequest,
  handleUnavailable,
} from '../reducerHandlers';

import {
  PIPELINE_MONITORING_AVAILABLE,
  PIPELINE_MONITORING_REQUEST,
  PIPELINE_MONITORING_UNAVAILABLE,
  SNACK_SET,
} from './constants';

const GET_SIGNED_BLOB = 'GET_SIGNED_BLOB';
const GET_SIGNED_BLOB_SUCCESS = 'GET_SIGNED_BLOB_SUCCESS';
const GET_SIGNED_BLOB_FAILURE = 'GET_SIGNED_BLOB_FAILURE';

const GET_OBJECT_NAMES = 'GET_OBJECT_NAMES';
const GET_OBJECT_NAMES_SUCCESS = 'GET_OBJECT_NAMES_SUCCESS';
const GET_OBJECT_NAMES_FAILURE = 'GET_OBJECT_NAMES_FAILURE';

const GET_ALL_METADATA = 'GET_ALL_METADATA';
const GET_ALL_METADATA_SUCCESS = 'GET_ALL_METADATA_SUCCESS';
const GET_ALL_METADATA_FAILURE = 'GET_ALL_METADATA_FAILURE';

const GET_SESSION_PROGRESS = 'GET_SESSION_PROGRESS';
const GET_SESSION_PROGRESS_SUCCESS = 'GET_SESSION_PROGRESS_SUCCESS';
const GET_SESSION_PROGRESS_FAILURE = 'GET_SESSION_PROGRESS_FAILURE';

const GET_TASKS_PROGRESS = 'GET_TASKS_PROGRESS';
const GET_TASKS_PROGRESS_SUCCESS = 'GET_TASKS_PROGRESS_SUCCESS';
const GET_TASKS_PROGRESS_FAILURE = 'GET_TASKS_PROGRESS_FAILURE';

const GET_SESSION_DATA = 'GET_SESSION_DATA';
const GET_SESSION_DATA_SUCCESS = 'GET_SESSION_DATA_SUCCESS';
const GET_SESSION_DATA_FAILURE = 'GET_SESSION_DATA_FAILURE';

const defaultState = {
  // Always empty now (pre-fetch flow removed); SessionView still passes it through to
  // the FlowChart request, which derives batches from Pipeline Manager when it is empty.
  batchesReceived: [],
  // Navigation data is no longer pre-fetched, so its loading flag stays false.
  loadingNavigationData: false,

  objectNames: {},
  objectNamesErrorMessage: null,
  loadingObjectNames: false,

  loadingAllMetadata: false,
  allMetadataErrorMessage: null,

  sessionProgress: {},
  loadingSessionProgress: false,
  sessionProgressErrorMessage: null,

  tasksProgress: {},
  loadingTasksProgress: false,
  tasksProgressErrorMessage: null,

  signedblob: null,
  isLoadingSignedBlob: null,

  ppNodes: null,
  isPPNodesLoading: false,
};

export const reducer = createReducer(defaultState, {
  [GET_SIGNED_BLOB]: handleGetSignedBlob,
  [GET_SIGNED_BLOB_SUCCESS]: handleSignedBlobSuccess,
  [GET_SIGNED_BLOB_FAILURE]: handleSignedBlobFailure,

  [GET_OBJECT_NAMES]: handleObjectNames,
  [GET_OBJECT_NAMES_FAILURE]: handleObjectNamesFailure,
  [GET_OBJECT_NAMES_SUCCESS]: handleObjectNamesSuccess,

  [GET_ALL_METADATA]: handleGetAllMetadata,
  [GET_ALL_METADATA_SUCCESS]: handleGetAllMetadataSuccess,
  [GET_ALL_METADATA_FAILURE]: handleGetAllMetadataFailure,

  [GET_SESSION_PROGRESS]: handleGetSessionProgress,
  [GET_SESSION_PROGRESS_SUCCESS]: handleGetSessionProgressSuccess,
  [GET_SESSION_PROGRESS_FAILURE]: handleGetSessionProgressFailure,

  [GET_TASKS_PROGRESS]: handleGetTasksProgress,
  [GET_TASKS_PROGRESS_SUCCESS]: handleGetTasksProgressSuccess,
  [GET_TASKS_PROGRESS_FAILURE]: handleGetTasksProgressFailure,

  [GET_SESSION_DATA]: handleGetSessionData,
  [GET_SESSION_DATA_SUCCESS]: handleGetSessionDataSuccess,
  [GET_SESSION_DATA_FAILURE]: handleGetSessionDataFailure,

  [PIPELINE_MONITORING_REQUEST]: handleRequest,
  [PIPELINE_MONITORING_AVAILABLE]: handleAvailable,
  [PIPELINE_MONITORING_UNAVAILABLE]: handleUnavailable,
});

//////////////////// GET ALL METADATA //////////////////////////
function handleGetAllMetadataSuccess(state, { payload: { objectNames } }) {
  return {
    ...state,
    loadingAllMetadata: false,
    objectNames,
  };
}

function handleGetAllMetadataFailure(state, { payload: { error } }) {
  console.log(error);
  return {
    ...state,
    loadingAllMetadata: false,
    allMetadataErrorMessage: error,
  };
}

function handleGetAllMetadata(state) {
  return {
    ...state,
    loadingAllMetadata: true,
    allMetadataErrorMessage: null,
  };
}

export function getAllMetadata() {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: GET_ALL_METADATA });
    try {
      const objectNames = await dataSource.getObjectNames();
      dispatch({
        type: GET_ALL_METADATA_SUCCESS,
        payload: { objectNames },
      });
    } catch (error) {
      dispatch({
        type: GET_ALL_METADATA_FAILURE,
        payload: { error },
      });
    }
  };
}
//////////////////// GET ALL METADATA //////////////////////////

//////////////////// GET OBJECT NAMES //////////////////////////
function handleObjectNames(state) {
  return {
    ...state,
    loadingObjectNames: true,
    objectNamesErrorMessage: null,
  };
}

function handleObjectNamesSuccess(state, { payload: { objectNames } }) {
  return {
    ...state,
    objectNames,
    loadingObjectNames: false,
    objectNamesErrorMessage: null,
  };
}

function handleObjectNamesFailure(state, { payload: { error } }) {
  console.log(error);
  return {
    ...state,
    loadingObjectNames: false,
    objectNamesErrorMessage: error,
  };
}

export function getObjectNames() {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: GET_OBJECT_NAMES });
    try {
      const objectNames = await dataSource.getObjectNames();
      dispatch({
        type: GET_OBJECT_NAMES_SUCCESS,
        payload: { objectNames },
      });
    } catch (error) {
      dispatch({
        type: GET_OBJECT_NAMES_FAILURE,
        payload: { error },
      });
    }
  };
}
//////////////////// GET OBJECT NAMES //////////////////////////

//////////////////// GET SESSION PROGRESS //////////////////////////
function handleGetSessionProgressSuccess(
  state,
  { payload: { sessionProgress } }
) {
  return {
    ...state,
    sessionProgress: sessionProgress,
    loadingSessionProgress: false,
    sessionProgressErrorMessage: null,
  };
}

function handleGetSessionProgressFailure(state, { payload: { error } }) {
  return {
    ...state,
    loadingSessionProgress: false,
    sessionProgressErrorMessage: error,
  };
}

function handleGetSessionProgress(state) {
  return {
    ...state,
    loadingSessionProgress: true,
    sessionProgressErrorMessage: null,
  };
}

export function getSessionProgress({ session, process_id }) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: GET_SESSION_PROGRESS });
    try {
      const sessionProgress = await dataSource.getSessionProgress(
        session,
        process_id
      );

      dispatch({
        type: GET_SESSION_PROGRESS_SUCCESS,
        payload: { sessionProgress },
      });
      return sessionProgress;
    } catch (error) {
      dispatch({
        type: GET_SESSION_PROGRESS_FAILURE,
        payload: { error },
      });
    }
  };
}

// Overlapping table requests (e.g. picking a date while a fetch is in flight)
// must resolve last-request-wins: older responses are dropped so they can never
// overwrite newer data or clear the loading flag early.
let sessionProgressTableRequestId = 0;

export function getSessionProgressTable({ date, store, robot, chain_code }) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    const keyState = 'sessionProgressTable';
    const loadingName = 'isSessionProgressTableLoading';
    const requestId = ++sessionProgressTableRequestId;
    dispatch({
      type: PIPELINE_MONITORING_REQUEST,
      payload: { loadingName: loadingName },
    });
    try {
      const response = await dataSource.getSessionProgressTable({
        date,
        store,
        robot,
        chain_code,
      });
      if (requestId !== sessionProgressTableRequestId) return;
      dispatch({
        type: PIPELINE_MONITORING_AVAILABLE,
        payload: {
          keyState: keyState,
          data: response,
          loadingName: loadingName,
          // Date the data belongs to, so views can tell stale from current.
          aditionalStates: { sessionProgressTableDate: date },
        },
      });
    } catch (error) {
      if (requestId !== sessionProgressTableRequestId) return;
      console.error('error: ', error);
      dispatch({
        type: PIPELINE_MONITORING_UNAVAILABLE,
        payload: {
          keyState: keyState,
          loadingName: loadingName,
          defaultState: [],
          // Mark the date as resolved so views show empty state, not a spinner.
          aditionalStates: { sessionProgressTableDate: date },
        },
      });
      const snack = {
        open: true,
        message: 'There was an error getting the session progress table.',
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}

//////////////////// GET SESSION PROGRESS //////////////////////////

//////////////////// GET TASKS PROGRESS //////////////////////////
function handleGetTasksProgressSuccess(state, { payload: { tasksProgress } }) {
  return {
    ...state,
    tasksProgress: tasksProgress,
    loadingTasksProgress: false,
    tasksProgressErrorMessage: null,
  };
}

function handleGetTasksProgressFailure(state, { payload: { error } }) {
  return {
    ...state,
    tasksProgress: null,
    loadingTasksProgress: false,
    tasksProgressErrorMessage: error,
  };
}

function handleGetTasksProgress(state) {
  return {
    ...state,
    loadingTasksProgress: true,
    tasksProgressErrorMessage: null,
  };
}

export function getTasksProgress({
  store,
  session_id,
  queue_name,
  project_id,
  service_name,
}) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: GET_TASKS_PROGRESS });
    try {
      const tasksProgress = await dataSource.getTasksProgress({
        store,
        session_id,
        queue_name,
        project_id,
        service_name,
      });

      dispatch({
        type: GET_TASKS_PROGRESS_SUCCESS,
        payload: { tasksProgress },
      });
    } catch (error) {
      dispatch({
        type: GET_TASKS_PROGRESS_FAILURE,
        payload: { error },
      });
    }
  };
}
//////////////////// GET TASKS PROGRESS //////////////////////////

function handleGetSessionDataSuccess(state, { payload: { sessionData } }) {
  return {
    ...state,
    sessionData: sessionData,
    loadingSessionData: false,
    sessionDataErrorMessage: null,
  };
}

function handleGetSessionDataFailure(state, { payload: { error } }) {
  return {
    ...state,
    loadingSessionData: false,
    sessionDataErrorMessage: error,
  };
}

function handleGetSessionData(state) {
  return {
    ...state,
    loadingSessionData: true,
    sessionDataErrorMessage: null,
  };
}

export function getSessionData({ session }) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: GET_SESSION_DATA });
    try {
      const sessionData = await dataSource.getSessionData({
        session,
      });

      dispatch({
        type: GET_SESSION_DATA_SUCCESS,
        payload: { sessionData },
      });
    } catch (error) {
      dispatch({
        type: GET_SESSION_DATA_FAILURE,
        payload: { error },
      });
    }
  };
}

//////////////////// GET TASKS PROGRESS //////////////////////////
function handleGetSignedBlob(state) {
  return {
    ...state,
    loadingSignedBlob: true,
    SignedBlobErrorMessage: null,
  };
}

function handleSignedBlobSuccess(state, { payload: { signedBlob } }) {
  return {
    ...state,
    loadingSignedBlob: false,
    signedBlob,
    SignedBlobErrorMessage: null,
  };
}

function handleSignedBlobFailure(state, { payload: { error } }) {
  return {
    ...state,
    loadingSignedBlob: false,
    SignedBlobErrorMessage: error,
  };
}

export function signBlob({ store, blobPath }) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({
      type: GET_SIGNED_BLOB,
    });
    try {
      const signedBlob = await dataSource.signBlob({ store, blobPath });
      dispatch({
        type: GET_SIGNED_BLOB_SUCCESS,
        payload: {
          signedBlob: signedBlob.signed_blob,
        },
      });
    } catch (error) {
      dispatch({
        type: GET_SIGNED_BLOB_FAILURE,
        payload: { error },
      });
    }
  };
}
//////////////////// GET TASKS PROGRESS //////////////////////////


export function getPPNodes(payload = { session: '', hard_finished_sessions: [] }) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    const keyState = 'ppNodes';
    const loadingName = 'isPPNodesLoading';
    dispatch({
      type: PIPELINE_MONITORING_REQUEST,
      payload: { loadingName: loadingName },
    });
    try {
      const response = await dataSource.getPPNodes(payload);
      dispatch({
        type: PIPELINE_MONITORING_AVAILABLE,
        payload: {
          keyState: keyState,
          data: response,
          loadingName: loadingName,
        },
      });
    } catch (error) {
      console.error('error: ', error);
      dispatch({
        type: PIPELINE_MONITORING_UNAVAILABLE,
        payload: {
          keyState: keyState,
          loadingName: loadingName,
          defaultState: [],
        },
      });
      const snack = {
        open: true,
        message: 'There was an error getting the pipeline progress nodes.',
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  }
}
